type Rgb = { r: number; g: number; b: number };

export type LogoAccentColors = {
  primaryColor: string;
  secondaryColor: string | null;
};

const MIN_SATURATION = 0.28;
const MIN_VALUE = 0.18;
const MAX_VALUE = 0.96;
const MIN_COLOR_DISTANCE = 58;

function rgbToHsv({ r, g, b }: Rgb) {
  const channels = [r, g, b].map((channel) => channel / 255);
  const max = Math.max(...channels);
  const min = Math.min(...channels);
  const delta = max - min;
  let hue = 0;

  if (delta > 0) {
    if (max === channels[0]) hue = ((channels[1] - channels[2]) / delta) % 6;
    else if (max === channels[1]) hue = (channels[2] - channels[0]) / delta + 2;
    else hue = (channels[0] - channels[1]) / delta + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }

  return { hue, saturation: max === 0 ? 0 : delta / max, value: max };
}

function rgbToHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b]
    .map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0'))
    .join('')}`;
}

function rgbDistance(a: Rgb, b: Rgb): number {
  return Math.sqrt(((a.r - b.r) ** 2) + ((a.g - b.g) ** 2) + ((a.b - b.b) ** 2));
}

function rgbToHsl({ r, g, b }: Rgb) {
  const channels = [r, g, b].map((channel) => channel / 255);
  const max = Math.max(...channels);
  const min = Math.min(...channels);
  const lightness = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) return { hue: 0, saturation: 0, lightness };

  const saturation = delta / (1 - Math.abs((2 * lightness) - 1));
  let hue = 0;
  if (max === channels[0]) hue = ((channels[1] - channels[2]) / delta) % 6;
  else if (max === channels[1]) hue = (channels[2] - channels[0]) / delta + 2;
  else hue = (channels[0] - channels[1]) / delta + 4;
  hue *= 60;
  if (hue < 0) hue += 360;
  return { hue, saturation, lightness };
}

function hslToRgb(hue: number, saturation: number, lightness: number): Rgb {
  const chroma = (1 - Math.abs((2 * lightness) - 1)) * saturation;
  const segment = hue / 60;
  const x = chroma * (1 - Math.abs((segment % 2) - 1));
  const [r1, g1, b1] = segment < 1
    ? [chroma, x, 0]
    : segment < 2
      ? [x, chroma, 0]
      : segment < 3
        ? [0, chroma, x]
        : segment < 4
          ? [0, x, chroma]
          : segment < 5
            ? [x, 0, chroma]
            : [chroma, 0, x];
  const match = lightness - (chroma / 2);
  return { r: (r1 + match) * 255, g: (g1 + match) * 255, b: (b1 + match) * 255 };
}

function buildCompanionColor(color: Rgb): string {
  const hsl = rgbToHsl(color);
  const companionLightness = hsl.lightness < 0.55
    ? Math.min(0.82, hsl.lightness + 0.2)
    : Math.max(0.24, hsl.lightness - 0.2);
  return rgbToHex(hslToRgb(hsl.hue, Math.max(0.35, hsl.saturation), companionLightness));
}

/**
 * Derives two saturated accent colors from an RGBA pixel buffer. This pure
 * function keeps the ranking rules testable independently of browser canvas.
 */
export function extractLogoAccentColorsFromPixels(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
): LogoAccentColors | null {
  if (!width || !height || pixels.length < 4) return null;

  const bins = new Map<string, { color: Rgb; score: number }>();
  for (let index = 0; index + 3 < pixels.length; index += 4) {
    const alpha = pixels[index + 3] / 255;
    if (alpha < 0.5) continue;

    const color = { r: pixels[index], g: pixels[index + 1], b: pixels[index + 2] };
    const { saturation, value } = rgbToHsv(color);
    // Transparent pixels, near-black backgrounds, near-white lettering, and
    // low-saturation gray/silver pixels are not useful stripe accents.
    if (value < MIN_VALUE || value > MAX_VALUE || saturation < MIN_SATURATION) continue;

    const quantized = {
      r: Math.min(255, Math.round(color.r / 16) * 16),
      g: Math.min(255, Math.round(color.g / 16) * 16),
      b: Math.min(255, Math.round(color.b / 16) * 16),
    };
    const key = `${quantized.r},${quantized.g},${quantized.b}`;
    const current = bins.get(key);
    const weight = alpha * (0.75 + saturation);
    if (current) {
      current.score += weight;
    } else {
      bins.set(key, { color: quantized, score: weight });
    }
  }

  const ranked = Array.from(bins.values()).sort((a, b) => b.score - a.score);
  const primary = ranked[0]?.color;
  if (!primary) return null;

  const secondary = ranked.slice(1).find(({ color }) => rgbDistance(primary, color) >= MIN_COLOR_DISTANCE)?.color;
  return {
    primaryColor: rgbToHex(primary),
    secondaryColor: secondary ? rgbToHex(secondary) : buildCompanionColor(primary),
  };
}

/**
 * Reads a newly selected logo in the browser and extracts its usable accent
 * colors. Failure is intentionally non-throwing so logo upload can continue.
 */
export async function extractLogoAccentColors(file: Blob): Promise<LogoAccentColors | null> {
  if (typeof document === 'undefined' || typeof Image === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const next = new Image();
      next.onload = () => resolve(next);
      next.onerror = () => reject(new Error('Unable to read logo pixels.'));
      next.src = objectUrl;
    });
    const sourceWidth = image.naturalWidth || image.width || 128;
    const sourceHeight = image.naturalHeight || image.height || 128;
    const scale = Math.min(1, 128 / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    return extractLogoAccentColorsFromPixels(context.getImageData(0, 0, width, height).data, width, height);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

const logoAccentMemoryCache = new Map<string, LogoAccentColors | null>();
const LOGO_ACCENT_CACHE_PREFIX = 'trade-proof:logo-accent-colors:v1:';

function logoAccentCacheKey(logoUrl: string, version?: string | null): string {
  return `${String(logoUrl || '').trim()}::${String(version || '').trim()}`;
}

function readStoredLogoAccentColors(value: string | null): LogoAccentColors | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<LogoAccentColors> & { none?: boolean };
    if (parsed.none === true) return null;
    if (!/^#[0-9a-f]{6}$/i.test(String(parsed.primaryColor || ''))) return null;
    if (parsed.secondaryColor != null && !/^#[0-9a-f]{6}$/i.test(String(parsed.secondaryColor || ''))) return null;
    return {
      primaryColor: String(parsed.primaryColor).toLowerCase(),
      secondaryColor: parsed.secondaryColor ? String(parsed.secondaryColor).toLowerCase() : null,
    };
  } catch {
    return null;
  }
}

export function getCachedLogoAccentColors(logoUrl: string, version?: string | null): LogoAccentColors | null {
  const key = logoAccentCacheKey(logoUrl, version);
  if (!key.startsWith('::') && logoAccentMemoryCache.has(key)) return logoAccentMemoryCache.get(key) || null;
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`${LOGO_ACCENT_CACHE_PREFIX}${key}`);
    if (raw) {
      const stored = readStoredLogoAccentColors(raw);
      logoAccentMemoryCache.set(key, stored);
      return stored;
    }
  } catch {
    // Browser storage may be unavailable; the in-memory cache still applies.
  }
  return null;
}

export function hasCachedLogoAccentColors(logoUrl: string, version?: string | null): boolean {
  const key = logoAccentCacheKey(logoUrl, version);
  if (key.startsWith('::')) return false;
  if (logoAccentMemoryCache.has(key)) return true;
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(`${LOGO_ACCENT_CACHE_PREFIX}${key}`) != null;
  } catch {
    return false;
  }
}

export function cacheLogoAccentColors(logoUrl: string, colors: LogoAccentColors | null | undefined, version?: string | null): void {
  const key = logoAccentCacheKey(logoUrl, version);
  if (key.startsWith('::')) return;
  const storedColors = colors?.primaryColor ? colors : null;
  logoAccentMemoryCache.set(key, storedColors);
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${LOGO_ACCENT_CACHE_PREFIX}${key}`, JSON.stringify(storedColors || { none: true }));
  } catch {
    // Browser storage may be unavailable; retain the in-memory result.
  }
}

export async function extractLogoAccentColorsFromUrl(logoUrl: string): Promise<LogoAccentColors | null> {
  const url = String(logoUrl || '').trim();
  if (!url || typeof fetch !== 'function') return null;
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) return null;
    return extractLogoAccentColors(await response.blob());
  } catch {
    return null;
  }
}

function parseHexColor(value: string): Rgb | null {
  const normalized = String(value || '').trim().replace(/^#/, '');
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function blendRgb(from: Rgb, to: Rgb, amount: number): Rgb {
  const ratio = Math.max(0, Math.min(1, amount));
  return {
    r: from.r + ((to.r - from.r) * ratio),
    g: from.g + ((to.g - from.g) * ratio),
    b: from.b + ((to.b - from.b) * ratio),
  };
}

export function buildLoginStripeGradient(colors: Pick<LogoAccentColors, 'primaryColor' | 'secondaryColor'> | null | undefined): string | null {
  const primaryColor = String(colors?.primaryColor || '').trim();
  if (!primaryColor) return null;
  const primaryRgb = parseHexColor(primaryColor);
  if (!primaryRgb) return null;
  const requestedSecondaryColor = String(colors?.secondaryColor || '').trim();
  const secondaryRgb = parseHexColor(requestedSecondaryColor)
    || parseHexColor(buildCompanionColor(primaryRgb));
  if (!secondaryRgb) return null;
  const secondaryColor = requestedSecondaryColor || rgbToHex(secondaryRgb);

  // Derive the shine from the dominant accent rather than averaging the two
  // ends. That keeps complementary logos from producing a muddy midpoint.
  const highlightRgb = blendRgb(primaryRgb, { r: 255, g: 255, b: 255 }, 0.88);
  const lightTintRgb = blendRgb(primaryRgb, highlightRgb, 0.62);
  const highlight = rgbToHex(highlightRgb);
  const lightTint = rgbToHex(lightTintRgb);
  const leftTransition = rgbToHex(blendRgb(primaryRgb, lightTintRgb, 0.5));
  const rightTransition = rgbToHex(blendRgb(secondaryRgb, lightTintRgb, 0.5));
  return `linear-gradient(90deg, ${primaryColor} 0%, ${primaryColor} 25%, ${leftTransition} 40%, ${lightTint} 46%, ${highlight} 50%, ${lightTint} 54%, ${rightTransition} 60%, ${secondaryColor} 75%, ${secondaryColor} 100%)`;
}
